import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { SocialShare } from '@/components/SocialShare';

const DigitalMinimalism = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Cost of Clutter: Why You Need Cal Newport's Digital Minimalism Now",
    "description": "Discover how digital minimalism can transform your relationship with technology and help you reclaim focus, presence, and meaningful connection.",
    "image": "https://mysticforest.club/lovable-uploads/digital-minimalism-hero.jpg",
    "author": {
      "@type": "Person",
      "name": "Piotr"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mysticforest.club/og-image.jpg"
      }
    },
    "datePublished": "2025-10-05",
    "dateModified": "2025-10-05"
  };

  return (
    <>
      <SEO
        title="The Cost of Clutter: Why You Need Cal Newport's Digital Minimalism Now"
        description="Discover how digital minimalism can transform your relationship with technology and help you reclaim focus, presence, and meaningful connection."
        canonicalUrl="https://mysticforest.club/articles/digital-minimalism"
        ogType="article"
        keywords={['digital minimalism', 'Cal Newport', 'digital detox', 'mindfulness', 'technology balance', 'offline life']}
        article={{
          publishedTime: '2025-10-05',
          modifiedTime: '2025-10-05',
          author: 'Piotr',
          section: 'Digital Wellness',
          tags: ['Digital Wellness', 'Mindfulness', 'Technology']
        }}
        breadcrumbs={[
          { name: 'Blog', url: '/blog' },
          { name: 'Digital Minimalism', url: '/articles/digital-minimalism' }
        ]}
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="max-w-6xl mx-auto px-6">
          <Breadcrumbs items={[
            { name: 'Blog', url: '/blog' },
            { name: 'Digital Minimalism', url: '/articles/digital-minimalism' }
          ]} />
        </div>
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Digital Wellness</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              The Cost of Clutter: Why You Need Cal Newport's Digital Minimalism Now
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>August 23, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>10 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <SocialShare 
              url="https://mysticforest.club/articles/digital-minimalism"
              title="The Cost of Clutter: Why You Need Cal Newport's Digital Minimalism Now"
              description="Discover how digital minimalism can transform your relationship with technology and help you reclaim focus, presence, and meaningful connection."
            />
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/digital-minimalism-hero.jpg" 
              alt="Person reading Cal Newport's Digital Minimalism book in peaceful natural setting, representing mindful technology use and offline living"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24" itemScope itemType="https://schema.org/Article">
        <meta itemProp="headline" content="The Cost of Clutter: Why You Need Cal Newport's Digital Minimalism Now" />
        <meta itemProp="datePublished" content="2025-10-05" />
        <meta itemProp="author" content="Piotr" />
        
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none" itemProp="articleBody">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              If your day is ruled by notifications and back-to-back meetings, and your evening dream is a "real reset, not just Netflix on the couch", you are the person we're talking to. You're the Overstimulated Professional—talented, active, but exhausted by post-pandemic overload and the dreaded Zoom fatigue.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <p>
                At <span className="text-accent">Mystic Forest</span>, we believe the answer isn't being "less online," but being deeply and intentionally offline. We create spaces that blend nature, mindfulness, and community to build a culture of calm and presence in a technologically overwhelmed world.
              </p>

              <p>
                This philosophy perfectly aligns with one of the most important books of our time: <strong className="text-accent">"Digital Minimalism: Choosing a Focused Life in a Noisy World"</strong> by Cal Newport.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Explorer's Manifesto: You Must Disconnect to Discover</h2>

              <p>
                Our core brand archetype is The Explorer. This is an invitation to break free and feel freedom in nature and within yourself. Newport's work gives us the perfect Explorer mantra: <em>"Don't live the life you're supposed to—live the life you want."</em> It's the ultimate fuel for the digital detox and slow-life movement.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Digital Minimalism is a Philosophy, Not a List of Hacks</h2>

              <p>
                Newport makes it clear: technology itself is neither good nor bad. The real shift isn't installing blocker apps—that's just a trick. The real key is establishing a philosophy of technology use rooted in your deepest values.
              </p>

              <p>A Digital Minimalist:</p>
              
              <ul className="space-y-3 ml-6">
                <li>Uses technology only to support something deeply valued (e.g., family connection).</li>
                <li>Confidently ignores everything else.</li>
              </ul>

              <p>
                <strong className="text-accent">The Mystic Forest connection:</strong> Everything we offer—yoga, meditation, circles—is curated and intentional to support your return to balance. We want you to apply that same level of intention to your digital life. Be the "director of new media in your own life".
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Price of Distraction: Clutter is Costly</h2>

              <p>
                For our main persona, the Overstimulated Professional who often says, "I don't have time," this is a pivotal insight.
              </p>

              <p>
                Newport quotes Thoreau: <em>"The cost of a thing is the amount of what I will call life is required to be exchanged for it, immediately or in the long run."</em>
              </p>

              <p>
                <strong className="text-accent">Your Cost:</strong> You exchange your life for digital clutter. This shows up as physical and psychological signals (stress, insomnia), relationship crises, and a deep longing for simplicity.
              </p>

              <p>
                <strong className="text-accent">The Mystic Forest Solution:</strong> We offer a holistic approach (body, mind, community, senses) focused on regeneration. We remove the clutter through our offline-first approach so you can focus on building meaningful relationships and a sense of purpose.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Solitude Deprivation</h2>

              <p>
                In an era of hyper-connectivity, we rarely spend time alone with our own thoughts and free from external input. The mind is often loud, and direction is lost.
              </p>

              <p>
                <strong className="text-accent">Newport's Insight:</strong> Solitude isn't about being alone; it's a state of mind free from the continuous stream of information. Technology limits the time you spend alone with your thoughts.
              </p>

              <p>
                <strong className="text-accent">The Mystic Forest Transformation (The Magician Archetype):</strong> The hunger for silence, nature, and presence is our priority. Our transformational retreats are an intentional digital declutter that truly shifts your perspective and allows you to go deeper.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Caregiver's Promise: "Can I really live without my phone?"</h2>

              <p>
                For many, the fear of "Can I manage without my phone?" is a major barrier. There's also the common fear of things being "too cult-like" or "too esoteric."
              </p>

              <p>
                Our Caregiver archetype addresses this fear directly: <em>"You can let go here. We will take care of you."</em>
              </p>

              <p>
                <span className="text-accent">Mystic Forest</span> offers curated experiences focused on safety and nurturing. Our offline-first approach is an invitation, not a pressure. You leave with new rhythms and the operating procedures (as Newport calls them) to maintain calm back in the city.
              </p>

              <p>
                If you've reached the turning point—the "I can't continue like this" moment—it's time to start a new beginning.
              </p>

              <p className="text-xl font-light italic text-accent">
                Action Step: We highly recommend reading "Digital Minimalism". Let it be the map for your next Offline-first retreat.
              </p>
            </div>
          </div>
        </div>
      </article>

        <Footer />
      </div>
    </>
  );
};

export default DigitalMinimalism;
